import type { User } from '@/types/directory/User';
import type { ObjectiveOrDraft } from '@/types/objectives';

import { getFullName } from '@/utils/user';

/**
 * The people an objective records: its owner, when it has one, then the participants listed
 * individually. Mirrors `ObjectiveHolders.Listed` on the server, and is the only place the order is
 * written -- an objective without an owner still has its participants, so no caller falls back to
 * an empty list and loses them along with the owner.
 */
export const objectiveHolderIds = (objective: ObjectiveOrDraft): string[] =>
    objective.ownerEmployeeId
        ? [objective.ownerEmployeeId, ...objective.participantEmployeeIds]
        : objective.participantEmployeeIds;

/**
 * The order an objective's people are shown in, on every screen: the reader first when they hold
 * it, then everyone else by name, the id settling namesakes. No one is shown as owning it: the
 * order the server lists them in, owner first, is ignored, so the same objective shows the same
 * faces in the same order wherever it is read.
 */
export const orderHoldersForReader = (holders: User[], readerId: string | undefined): User[] => {
    const rank = (holder: User): number => (holder.id === readerId ? 0 : 1);

    return [...holders].sort(
        (a, b) =>
            rank(a) - rank(b) ||
            getFullName(a).localeCompare(getFullName(b), undefined, { sensitivity: 'base' }) ||
            a.id.localeCompare(b.id),
    );
};

/**
 * The holders left when one is removed while an objective is edited, owner first. Removing the
 * owner hands the objective to the first remaining holder within the reader's reach, moved to the
 * front; emptying the list is not a handover, the reader then picks a new owner. `undefined` when
 * the owner must stay: no one else within the reader's reach could take the objective.
 */
export const removeObjectiveHolder = (
    holderIds: string[],
    removedId: string,
    isWithinReach: (employeeId: string) => boolean,
): string[] | undefined => {
    const remaining = holderIds.filter((id) => id !== removedId);
    if (removedId !== holderIds[0] || remaining.length === 0) return remaining;

    const nextOwnerId = remaining.find(isWithinReach);
    if (!nextOwnerId) return undefined;

    return [nextOwnerId, ...remaining.filter((id) => id !== nextOwnerId)];
};

/**
 * Whether the holder at `index` may be removed: anyone after the owner; the owner only when the
 * reader may hand objectives over and reaches them -- the server refuses to take an objective from
 * someone the reader does not reach.
 */
export const isObjectiveHolderRemovable = ({
    holderId,
    index,
    isOwnerLocked,
    isWithinReach,
}: {
    holderId: string;
    index: number;
    isOwnerLocked: boolean;
    isWithinReach: (employeeId: string) => boolean;
}): boolean => index > 0 || (!isOwnerLocked && isWithinReach(holderId));
